import axios from "axios";
import { DocumentDesignState } from "../canvas/state/types";
import { CanvasPaperConfig } from "../designer/PageDesigner/PageDesigner";
import {
  Application,
  BaseRecord,
  CheckAuthResponse,
  DocumentDesign,
  FilestackConfig,
  Page,
} from "./types";

import {
  getPaperConfig,
  getPartialDocument,
} from "../designer/PageDesigner/partial-document";
import { SortRule, Filter } from "@smartsuite/types";

export function setClientDefaults(baseUrl: string, accountId: string): void {
  if (baseUrl && !baseUrl.includes("localhost")) {
    axios.defaults.baseURL = baseUrl;
  }

  axios.defaults.withCredentials = true;
  axios.defaults.headers["ACCOUNT-ID"] = accountId;
}

export async function fetchApplication(appId: string): Promise<Application> {
  const response = await axios.get(`/api/v1/applications/${appId}/`);
  return response.data;
}

export async function fetchRecordsTextJson(
  appId: string,
  visibleFields: string[],
  recordIds?: string[],
  filterPayload?: Filter,
  sort?: SortRule[],
): Promise<RecordTextJson[]> {
  const response = await axios.post(
    `${`/api/v1/applications/${appId}/records/generate_json/`}`,
    {
      visible_fields: visibleFields,
      records_ids: recordIds,
      filter: filterPayload,
      sort,
    },
  );
  return response.data;
}

export async function fetchRecordByIndex(
  appId: string,
  index: number,
): Promise<Page<BaseRecord>> {
  const queryParams = new URLSearchParams();
  queryParams.append("limit", "1");
  queryParams.append("offset", `${index}`);

  const response = await axios.post(
    `${`/api/v1/applications/${appId}/records/list/`}?${queryParams}`,
  );
  return response.data;
}

export async function fetchRecordById(
  appId: string,
  recordId: string,
): Promise<BaseRecord> {
  const response = await axios.get(
    `/api/v1/applications/${appId}/records/${recordId}/`,
  );
  return response.data;
}

export async function fetchDocumentDesignById(
  documentDesignId: string,
): Promise<DocumentDesign> {
  const response = await axios.get(
    `/api/v1/document-designs/${documentDesignId}/`,
  );
  return response.data;
}

export async function createDocumentDesign(
  appId: string,
  documentName: string,
  state: DocumentDesignState,
  paperConfig: CanvasPaperConfig,
): Promise<DocumentDesign> {
  const response = await axios.post("/api/v1/document-designs/", {
    name: documentName,
    canvas_template: state,
    application: appId,
    paper_config: getPaperConfig(paperConfig),
  });
  return response.data;
}

export async function updateDocumentDesign(
  documentDesign: Partial<DocumentDesign>,
): Promise<DocumentDesign> {
  const response = await axios.patch(
    `/api/v1/document-designs/${documentDesign.id}/`,
    documentDesign,
  );
  return response.data;
}

export async function deleteDocumentDesign(
  documentDesignId: string,
): Promise<void> {
  await axios.delete(`/api/v1/document-designs/${documentDesignId}/`);
}

export type RecordTextJson = Record<string, string>;

export interface Member {
  id: string;
  full_name: Record<string, string>;
}

export async function fetchMembers(): Promise<Map<string, string>> {
  const response = await axios.post(
    `/api/v1/applications/members/records/list/`,
  );
  const members = new Map();
  response.data.items.forEach((item: Member) => {
    members.set(item.id, item.full_name.sys_root);
  });
  return members;
}

export async function fetchCheckAuth(): Promise<CheckAuthResponse> {
  const response = await axios.get(`/api/v1/users/check_auth/`);
  return response.data;
}

export async function fetchFilestackConfig(): Promise<FilestackConfig> {
  const response = await axios.get("/api/v1/shared-files/filestack/");
  return response.data;
}

export async function saveOrUpdateDocument(
  state: DocumentDesignState,
  canvasPaperConfig: CanvasPaperConfig,
  appId: string,
  documentName: string,
  documentDesign?: DocumentDesign,
): Promise<DocumentDesign> {
  if (documentDesign?.id) {
    return await updateDocumentDesign(
      getPartialDocument(documentDesign, state, canvasPaperConfig),
    );
  } else {
    return await createDocumentDesign(
      appId,
      documentName,
      state,
      canvasPaperConfig,
    );
  }
}
